import { memo, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { modalities } from "../data/modalities";
import { services } from "../data/services";

const NAV_LINKS = [
  { name: "Home", path: "/" },
  { name: "Contact", path: "/contact" },
];

function NavbarPremium() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(null);
  const dropdownsRef = useRef(null);

  useEffect(() => {
    let ticking = false;

    const updateScrollState = () => {
      setScrolled(window.scrollY > 20);
      ticking = false;
    };

    const handleScroll = () => {
      if (ticking) {
        return;
      }

      ticking = true;
      window.requestAnimationFrame(updateScrollState);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    updateScrollState();

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (dropdownsRef.current && !dropdownsRef.current.contains(event.target)) {
        setOpen(null);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);

    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  return (
    <nav
      className={`fixed top-0 z-50 w-full text-black transition-colors duration-300 ${
        scrolled ? "bg-white/80 shadow-md backdrop-blur-md" : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-5">
        <Link to="/" className="flex items-center">
          <img
            src="/assets/logo-24-night-spa.png"
            alt="24 Night Spa Pune"
            width="220"
            height="56"
            loading="eager"
            decoding="async"
            className="h-14 object-contain md:h-14"
          />
        </Link>

        <div
          className="hidden gap-8 text-sm uppercase tracking-widest md:flex"
          ref={dropdownsRef}
        >
          <Link
            to={NAV_LINKS[0].path}
            className="transition-opacity hover:opacity-70"
          >
            {NAV_LINKS[0].name}
          </Link>

          <div className="relative">
            <button
              type="button"
              onClick={() =>
                setOpen((prevOpen) =>
                  prevOpen === "services" ? null : "services"
                )
              }
              className="cursor-pointer transition-opacity hover:opacity-70"
              aria-expanded={open === "services"}
              aria-haspopup="true"
            >
              Services
            </button>

            {open === "services" && (
              <div className="absolute left-1/2 top-full mt-4 max-h-[28rem] w-[22rem] -translate-x-1/2 overflow-y-auto rounded-3xl border border-gray-200 bg-white p-3 shadow-xl transition-all duration-200">
                <div className="space-y-1 text-[11px] font-medium uppercase tracking-[0.22em] text-gray-500">
                  {services.map((service) => (
                    <Link
                      key={service.id}
                      to={`/service/${service.slug}`}
                      onClick={() => setOpen(null)}
                      className="block rounded-2xl border border-transparent px-4 py-3 text-left text-gray-700 transition-colors hover:border-gray-200 hover:bg-[#fff8f8]"
                    >
                      {service.title}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="relative">
            <button
              type="button"
              onClick={() =>
                setOpen((prevOpen) =>
                  prevOpen === "modalities" ? null : "modalities"
                )
              }
              className="cursor-pointer transition-opacity hover:opacity-70"
              aria-expanded={open === "modalities"}
              aria-haspopup="true"
            >
              Modalities
            </button>

            {open === "modalities" && (
              <div className="absolute left-1/2 top-full mt-4 max-h-[28rem] w-[22rem] -translate-x-1/2 overflow-y-auto rounded-3xl border border-gray-200 bg-white p-3 shadow-xl transition-all duration-200">
                <div className="space-y-1 text-[11px] font-medium uppercase tracking-[0.22em] text-gray-500">
                  {modalities.map((modality) => (
                    <Link
                      key={modality.id}
                      to={`/modality/${modality.slug}`}
                      onClick={() => setOpen(null)}
                      className="block rounded-2xl border border-transparent px-4 py-3 text-left text-gray-700 transition-colors hover:border-gray-200 hover:bg-[#fff8f8]"
                    >
                      {modality.title}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          <Link
            to={NAV_LINKS[1].path}
            className="transition-opacity hover:opacity-70"
          >
            {NAV_LINKS[1].name}
          </Link>
        </div>

        <button
          onClick={() => setIsOpen((prevOpen) => !prevOpen)}
          className="flex flex-col gap-1 md:hidden"
          aria-label="Toggle menu"
        >
          <span className="h-[2px] w-6 bg-black" />
          <span className="h-[2px] w-6 bg-black" />
          <span className="h-[2px] w-6 bg-black" />
        </button>
      </div>

      {isOpen && (
        <div
          className="space-y-5 border-t border-gray-200 bg-white py-6 text-center text-black md:hidden"
          ref={dropdownsRef}
        >
          <Link
            to={NAV_LINKS[0].path}
            onClick={() => setIsOpen(false)}
            className="block"
          >
            {NAV_LINKS[0].name}
          </Link>

          <div className="mx-6 rounded-3xl border border-gray-200 bg-[#fff8f8] p-4 text-left">
            <p className="mb-3 text-center text-xs font-semibold uppercase tracking-[0.25em] text-gray-500">
              <button
                type="button"
                onClick={() =>
                  setOpen((prevOpen) =>
                    prevOpen === "services" ? null : "services"
                  )
                }
                className="cursor-pointer"
                aria-expanded={open === "services"}
                aria-haspopup="true"
              >
                Services
              </button>
            </p>
            {open === "services" && (
              <div className="space-y-2 transition-all duration-200">
                {services.map((service) => (
                  <Link
                    key={service.id}
                    to={`/service/${service.slug}`}
                    onClick={() => {
                      setOpen(null);
                      setIsOpen(false);
                    }}
                    className="block rounded-2xl bg-white px-4 py-3 text-sm text-gray-700"
                  >
                    {service.title}
                  </Link>
                ))}
              </div>
            )}

            <p className="mb-3 mt-5 text-center text-xs font-semibold uppercase tracking-[0.25em] text-gray-500">
              <button
                type="button"
                onClick={() =>
                  setOpen((prevOpen) =>
                    prevOpen === "modalities" ? null : "modalities"
                  )
                }
                className="cursor-pointer"
                aria-expanded={open === "modalities"}
                aria-haspopup="true"
              >
                Modalities
              </button>
            </p>
            {open === "modalities" && (
              <div className="space-y-2 transition-all duration-200">
                {modalities.map((modality) => (
                  <Link
                    key={modality.id}
                    to={`/modality/${modality.slug}`}
                    onClick={() => {
                      setOpen(null);
                      setIsOpen(false);
                    }}
                    className="block rounded-2xl bg-white px-4 py-3 text-sm text-gray-700"
                  >
                    {modality.title}
                  </Link>
                ))}
              </div>
            )}
          </div>

          <Link
            to={NAV_LINKS[1].path}
            onClick={() => setIsOpen(false)}
            className="block"
          >
            {NAV_LINKS[1].name}
          </Link>
        </div>
      )}
    </nav>
  );
}

export default memo(NavbarPremium);
